import { useRef, useCallback, useState } from 'react';
interface Props {
  min: number;
  max: number;
  low: number;
  high: number;
  onChangeLow: (v: number) => void;
  onChangeHigh: (v: number) => void;
  width?: number;
  step?: number;
  color?: string;
}
export function RangeSlider({
  min,
  max,
  low,
  high,
  onChangeLow,
  onChangeHigh,
  width = 120,
  step = 1,
  color = '#3B82F6'
}: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeThumb, setActiveThumb] = useState<'low' | 'high' | null>(null);
  const pct = (v: number) => (v - min) / (max - min) * 100;
  const handlePointer = useCallback((thumb: 'low' | 'high', e: React.PointerEvent) => {
    e.preventDefault();
    setActiveThumb(thumb);
    const track = trackRef.current;
    if (!track) return;
    const set = thumb === 'low' ? onChangeLow : onChangeHigh;
    const other = thumb === 'low' ? high : low;
    const el = e.target as HTMLElement;
    el.setPointerCapture(e.pointerId);
    const onMove = (ev: PointerEvent) => {
      const rect = track.getBoundingClientRect();
      const raw = (ev.clientX - rect.left) / rect.width * (max - min) + min;
      const snapped = Math.round(raw / step) * step;
      const clamped = Math.min(Math.max(snapped, min), max);
      if (thumb === 'low') {
        set(Math.min(clamped, other));
      } else {
        set(Math.max(clamped, other));
      }
    };
    const onUp = () => {
      setActiveThumb(null);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [min, max, low, high, onChangeLow, onChangeHigh, step]);
  const left = pct(low);
  const right = pct(high);

  // Smart z-index: ensure both thumbs remain accessible when overlapping
  const range = max - min;
  const thumbsOverlap = Math.abs(high - low) <= range * 0.02;
  let lowZIndex = 2;
  let highZIndex = 2;
  if (activeThumb === 'low') {
    // Low thumb being dragged - always on top
    lowZIndex = 4;
    highZIndex = 2;
  } else if (activeThumb === 'high') {
    // High thumb being dragged - always on top
    lowZIndex = 2;
    highZIndex = 4;
  } else if (thumbsOverlap) {
    // When overlapping and idle: determine which thumb has more room to move
    const lowCanMoveLeft = low - min > range * 0.01;
    const highCanMoveRight = max - high > range * 0.01;

    // If low is at/near min, prioritize high thumb (it's the only one that can move)
    // If high is at/near max, prioritize low thumb (it's the only one that can move)
    // If both in middle, prioritize the one with less room (more constrained)
    if (!lowCanMoveLeft && highCanMoveRight) {
      // Low is stuck at min, high can move right
      lowZIndex = 2;
      highZIndex = 3;
    } else if (lowCanMoveLeft && !highCanMoveRight) {
      // High is stuck at max, low can move left
      lowZIndex = 3;
      highZIndex = 2;
    } else {
      // Both can move - use midpoint logic
      const midpoint = min + range / 2;
      const lowDistFromMid = Math.abs(low - midpoint);
      const highDistFromMid = Math.abs(high - midpoint);
      if (lowDistFromMid <= highDistFromMid) {
        lowZIndex = 3;
        highZIndex = 2;
      } else {
        lowZIndex = 2;
        highZIndex = 3;
      }
    }
  }
  return <div style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    width,
    position: 'relative'
  }} data-id="element-1185" data-mp="ef8v0utruc63">
      <div ref={trackRef} style={{
      position: 'relative',
      height: 6,
      borderRadius: 3,
      flex: 1,
      background: '#e5e7eb',
      cursor: 'pointer'
    }} data-id="element-1186" data-mp="ndkjorb2uzvw">
        {/* active fill */}
        <div style={{
        position: 'absolute',
        top: 0,
        height: '100%',
        borderRadius: 3,
        left: `${left}%`,
        width: `${right - left}%`,
        background: color
      }} data-id="element-1187" data-mp="x2uc689u2kkj" />
        {/* low thumb */}
        <div onPointerDown={e => handlePointer('low', e)} style={{
        position: 'absolute',
        top: '50%',
        left: `${left}%`,
        transform: 'translate(-50%, -50%)',
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: '#fff',
        border: `2px solid ${color}`,
        cursor: 'grab',
        touchAction: 'none',
        zIndex: lowZIndex
      }} data-id="element-1188" data-mp="164ttz2so9soj" />
        {/* high thumb */}
        <div onPointerDown={e => handlePointer('high', e)} style={{
        position: 'absolute',
        top: '50%',
        left: `${right}%`,
        transform: 'translate(-50%, -50%)',
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: '#fff',
        border: `2px solid ${color}`,
        cursor: 'grab',
        touchAction: 'none',
        zIndex: highZIndex
      }} data-id="element-1189" data-mp="3eam80zsqawxf" />
      </div>
    </div>;
}